import {
  BadRequestException,
  Body,
  Controller,
  Get,
  Param,
  Post,
  Query,
  Req,
  UseGuards,
} from '@nestjs/common';
import { ApiBearerAuth, ApiOperation, ApiTags } from '@nestjs/swagger';
import { AuthGuard, GuardedRequest } from '../auth/auth.guard';
import { DemoModeGuard } from '../auth/demo-mode.guard';
import { LocalDiskRetirementService } from './local-disk-retirement.service';
import { PlatformStorageAdminService } from './platform-storage-admin.service';

/**
 * Platform-admin storage controls.
 *
 * What an admin can do here: change a merchant's plan, raise their storage
 * allowance when they pay, grant BYOS, see where media lives, repair a drifted
 * counter, and force a merchant back onto platform storage in a support
 * emergency.
 *
 * What an admin cannot do here, at all: read a merchant's storage credentials.
 * There is no endpoint for it. The closest thing is the audited break-glass
 * reveal below, which returns the bucket name and a masked key id — never the
 * secret, which has no read path anywhere in this codebase — and emails the
 * merchant that a named admin looked.
 */
@ApiTags('Platform · Storage')
@ApiBearerAuth()
@Controller('platform/storage')
@UseGuards(AuthGuard, DemoModeGuard)
export class PlatformStorageController {
  constructor(
    private readonly admin: PlatformStorageAdminService,
    private readonly retirement: LocalDiskRetirementService
  ) {}

  private actor(req: GuardedRequest): string {
    return req.user?.id ?? req.user?.email ?? 'system';
  }

  @Get('fleet')
  @ApiOperation({ summary: 'Platform-wide storage totals, health and cost estimate' })
  fleet() {
    return this.admin.fleetOverview();
  }

  @Get('merchants')
  @ApiOperation({ summary: 'Per-merchant storage usage, sorted by size' })
  merchants(@Query('limit') limit?: string) {
    return this.admin.merchantUsage(limit ? Number(limit) : 50);
  }

  @Get('merchants/:organizationId')
  @ApiOperation({ summary: 'One merchant’s backends — no credentials, no bucket names' })
  merchant(@Param('organizationId') organizationId: string) {
    return this.admin.merchantStorage(organizationId);
  }

  /**
   * The paid top-up lever. Billing integration does not exist yet, so the flow
   * is: merchant pays out of band → admin raises the cap here → the audit
   * record carries who raised it, when, from what, and to what. That record is
   * the thing that settles a dispute three months later.
   */
  @Post('merchants/:organizationId/quota')
  @ApiOperation({ summary: 'Change plan and/or storage allowance' })
  async setQuota(
    @Param('organizationId') organizationId: string,
    @Body() body: Record<string, unknown>,
    @Req() req: GuardedRequest
  ) {
    const plan = body['plan'] != null ? String(body['plan']) : undefined;
    const storageCapBytes =
      body['storageCapBytes'] === null
        ? null
        : body['storageCapBytes'] != null
          ? Number(body['storageCapBytes'])
          : undefined;

    if (plan === undefined && storageCapBytes === undefined) {
      throw new BadRequestException('Provide plan and/or storageCapBytes');
    }
    return this.admin.setQuota(organizationId, { plan, storageCapBytes }, this.actor(req), body['reason'] ? String(body['reason']) : undefined);
  }

  @Post('merchants/:organizationId/byos')
  @ApiOperation({ summary: 'Grant or revoke bring-your-own-storage' })
  setByos(
    @Param('organizationId') organizationId: string,
    @Body() body: Record<string, unknown>,
    @Req() req: GuardedRequest
  ) {
    if (typeof body['enabled'] !== 'boolean') throw new BadRequestException('enabled (boolean) is required');
    return this.admin.setByosEnabled(organizationId, body['enabled'], this.actor(req));
  }

  @Post('merchants/:organizationId/recompute')
  @ApiOperation({ summary: 'Recompute a merchant’s usage totals from the registry' })
  recompute(@Param('organizationId') organizationId: string, @Req() req: GuardedRequest) {
    return this.admin.recomputeUsage(organizationId, this.actor(req));
  }

  /**
   * Support escape hatch: move a merchant's write target back to platform
   * storage without touching a single existing object. Their BYOS media keeps
   * serving from their bucket exactly as before.
   */
  @Post('merchants/:organizationId/force-platform-storage')
  forcePlatform(@Param('organizationId') organizationId: string, @Req() req: GuardedRequest) {
    return this.admin.forcePlatformStorage(organizationId, this.actor(req));
  }

  /**
   * Break-glass reveal (plan §10.4).
   *
   * Returns bucket name and masked access key id — nothing else, and never the
   * secret. Requires a typed justification, writes an audit record naming the
   * admin, and notifies the merchant by email that their storage config was
   * viewed. If you are here because support needs to debug a connection, the
   * health state and error classification on the merchant row will almost
   * always tell you more than the bucket name will.
   */
  @Post('merchants/:organizationId/configs/:configId/reveal')
  @ApiOperation({ summary: 'Audited break-glass reveal of bucket identity (never secrets)' })
  reveal(
    @Param('organizationId') organizationId: string,
    @Param('configId') configId: string,
    @Body() body: Record<string, unknown>,
    @Req() req: GuardedRequest
  ) {
    const justification = String(body['justification'] ?? '').trim();
    if (justification.length < 20) {
      throw new BadRequestException(
        'A written justification of at least 20 characters is required to reveal a merchant storage config.'
      );
    }
    return this.admin.breakGlassReveal(organizationId, configId, justification, this.actor(req));
  }

  @Get('reconciliation/:configId')
  @ApiOperation({ summary: 'Orphan/missing object report for one backend (report only)' })
  reconciliation(@Param('configId') configId: string) {
    return this.admin.reconcile(configId);
  }

  @Post('backup/drill')
  @ApiOperation({ summary: 'Run a restore drill now' })
  drill() {
    return this.admin.restoreDrill();
  }

  // --- Local-disk retirement (one-off, §13) ---------------------------------

  @Get('local-disk/plan')
  @ApiOperation({ summary: 'Dry run: what is still on local disk, and where legacy URLs live' })
  retirementPlan() {
    return this.retirement.plan();
  }

  @Post('local-disk/retire/:organizationId')
  @ApiOperation({ summary: 'Copy one merchant off local disk onto platform R2' })
  retireOne(@Param('organizationId') organizationId: string, @Req() req: GuardedRequest) {
    return this.retirement.retireOrganization(organizationId, this.actor(req));
  }

  @Post('local-disk/retire-all')
  @ApiOperation({ summary: 'Copy every merchant off local disk (sequential, resumable)' })
  async retireAll(@Req() req: GuardedRequest) {
    // Long-running by nature — started in the background, with progress
    // readable through the migration job rows.
    const actorId = this.actor(req);
    void this.retirement.retireAll(actorId);
    return { started: true, message: 'Retirement started — poll /platform/storage/local-disk/plan for progress' };
  }
}
